import type { ComponentProps } from "react";
import { styled } from "tamagui";
import { Progress as TamaguiProgress } from "@tamagui/progress";

// The neutral indicator is invisible on `$surfaceInverse`: `$surfaceStrong` and
// green900 have the same relative luminance, so the fill vanishes into the
// track. The dark tone swaps both to a green pair — a dim track with the same
// lime fill the brand button uses — rather than leaving a grey bar on green.
const toneVariants = {
  light: {
    backgroundColor: "$surface0Hover"
  },
  dark: {
    backgroundColor: "$borderPositiveStrong"
  }
} as const;
const indicatorToneVariants = {
  light: {
    backgroundColor: "$surfaceStrong"
  },
  dark: {
    backgroundColor: "$bgBrandStrong"
  }
} as const;
const StyledProgress = styled(TamaguiProgress, {
  borderRadius: "$brFull",
  height: "$si0.75",
  overflow: "hidden",
  width: "100%",
  // Tamagui's Progress `size` variant sets a minWidth floor (size * 20).
  // Zero it out so the bar can be constrained by its parent.
  minWidth: 0,
  variants: {
    tone: toneVariants
  },
  defaultVariants: {
    tone: "light"
  }
});
const StyledIndicator = styled(TamaguiProgress.Indicator, {
  borderRadius: "$brFull",
  variants: {
    tone: indicatorToneVariants
  },
  defaultVariants: {
    tone: "light"
  }
});
type StyledProgressProps = ComponentProps<typeof StyledProgress>;
export type ProgressBarProps = Omit<StyledProgressProps, "value"> & {
  /**
   * Current progress value (0–100).
   */
  value: number;
  /**
   * Accessible label describing the progress.
   * Defaults to a percentage string (e.g. "50%").
   */
  getValueLabel?: (value: number, max: number) => string;
  /** Surface the bar sits on. @default "light" */
  tone?: "light" | "dark";
};
export const ProgressBar = ({
  value,
  getValueLabel,
  tone = "light",
  ...rest
}: ProgressBarProps) => {
  return <StyledProgress tone={tone} {...rest} value={value} getValueLabel={getValueLabel} data-sentry-element="StyledProgress" data-sentry-component="ProgressBar" data-sentry-source-file="ProgressBar.tsx">
      <StyledIndicator tone={tone} transition="300ms ease" data-sentry-element="StyledIndicator" data-sentry-source-file="ProgressBar.tsx" />
    </StyledProgress>;
};